/* global Excel */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  fetchTier1Catalogs,
  fetchTier1BaseWorks,
  fetchTier1WorkGroups,
  resolveBaseWorkRequirements,
} from "../api/tier1Api";
import { fetchOffers, fetchProducts } from "../api/resourceCatalogApi";

const TYPES = [
  { id: "LABOUR",    label: "ხელფასი",   color: "#2196F3" },
  { id: "EQUIPMENT", label: "მექანიზმი", color: "#FF9800" },
  { id: "MATERIAL",  label: "მასალა",    color: "#4CAF50" },
];

const LANGS = [
  { id: "geo", label: "ქართ." },
  { id: "eng", label: "ENG" },
  { id: "rus", label: "РУС" },
];

const LEVELS = [1, 2, 3];
const EMPTY_LEVEL_VALUE = "—";
const VARIANT_STEP_KEY = "V";

const pickName = (item, lang) =>
  item?.[`name_${lang}`] || item?.name_geo || item?.name_eng || item?.name_rus || "";

const pickGroupName = (bw, lang) =>
  bw?.[`work_group_name_${lang}`] || bw?.work_group_name_geo || bw?.work_group_name_eng || "";

const levelValue = (bw, level, lang) =>
  bw?.[`level${level}_value_${lang}`] || bw?.[`level${level}_value_geo`] || "";

const levelParam = (group, level, lang) =>
  group?.[`level${level}_param_${lang}`] || group?.[`level${level}_param_geo`] || "";

const formatBaseUnitDisplay = (bw) => {
  const v = Number(bw?.base_unit_value);
  const u = bw?.base_unit || "";
  return Number.isFinite(v) && v > 0 && v !== 1 ? `${v} ${u}`.trim() : u;
};

const formatMoney = (value) =>
  `${Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₾`;

const isAreaUnit = (unit) => /^(m2|м2|m²|м²|მ²|მ2|sqm)$/i.test(String(unit || "").trim());

function distinct(values) {
  return [...new Set(values)];
}

/**
 * Walks the parametric levels of a work group. Each level offers the distinct
 * values left after the earlier choices; a level with one value fills itself.
 * If levels cannot tell the remaining variants apart, a final variant step is added.
 */
function deriveSteps(works, group, choices, lang) {
  let candidates = works;
  const steps = [];

  for (const level of LEVELS) {
    const raw = candidates.map((bw) => levelValue(bw, level, lang));
    if (raw.every((v) => !v)) continue;
    const options = distinct(raw.map((v) => v || EMPTY_LEVEL_VALUE));
    const key = `L${level}`;
    const auto = options.length === 1;
    const chosen = auto ? options[0] : choices[key];
    steps.push({
      key,
      kind: "level",
      label: levelParam(group, level, lang) || `დონე ${level}`,
      tag: `დონე ${level}`,
      options: options.map((v) => ({ value: v, label: v })),
      auto,
      chosen,
    });
    if (!chosen) return { steps, resolved: null };
    candidates = candidates.filter((bw) => (levelValue(bw, level, lang) || EMPTY_LEVEL_VALUE) === chosen);
  }

  if (candidates.length === 1) return { steps, resolved: candidates[0] };
  if (candidates.length === 0) return { steps, resolved: null };

  const chosenVariant = choices[VARIANT_STEP_KEY];
  steps.push({
    key: VARIANT_STEP_KEY,
    kind: "level",
    label: "ვარიანტი",
    tag: "ვარიანტი",
    options: candidates.map((bw) => ({ value: String(bw.id), label: pickName(bw, lang) || bw.code })),
    auto: false,
    chosen: chosenVariant,
  });
  const resolved = candidates.find((bw) => String(bw.id) === String(chosenVariant)) || null;
  return { steps, resolved };
}

function groupSearchResults(baseWorks, lang) {
  const byGroup = new Map();
  baseWorks.forEach((bw) => {
    const key = bw.work_group_id;
    if (!byGroup.has(key)) {
      byGroup.set(key, {
        id: key,
        code: bw.work_group_code,
        name: pickGroupName(bw, lang) || pickName(bw, lang) || bw.work_group_code,
        unit: formatBaseUnitDisplay(bw),
        count: 0,
      });
    }
    byGroup.get(key).count += 1;
  });
  return [...byGroup.values()];
}

function mapResolvedItem(it) {
  return {
    id: it.requirement_id,
    mode: it.mode,
    code: it.product?.display_id || it.classification_code || `req#${it.requirement_id}`,
    product_id: it.product?.id || null,
    classification_code: it.classification_code,
    resource_type: it.kind,
    name_geo: it.product?.name_geo || it.classification_name_geo,
    name_eng: it.product?.name_eng || it.classification_name_eng,
    name_rus: it.product?.name_rus || it.classification_name_rus,
    // Price and quantity must share one unit: the server converts the requirement
    // quantity into the product's priced unit.
    base_unit: it.price_unit || it.unit_code || it.product?.base_unit || "",
    unit_mismatch: !!it.unit_mismatch,
    unit_converted: !!it.unit_converted,
    requirement_unit: it.unit_code || "",
    is_generic: it.mode === "generic" || it.product?.is_generic || false,
    missing_product: !it.product,
    quantity_per_unit: it.quantity_in_product_unit ?? it.quantity_per_base_unit,
    unit_rate: it.best_offer?.unit_rate ?? null,
    currency: it.best_offer?.currency,
    supplier_name: it.best_offer?.supplier_name,
    is_preferred: it.best_offer?.is_preferred,
  };
}

async function findInsertRow(sheet, context, rowsToInsert) {
  const used = sheet.getUsedRangeOrNullObject();
  used.load(["rowCount", "columnCount", "values"]);
  await context.sync();
  if (used.isNullObject || used.rowCount < 9) return 9;

  const summaryKeywords = ["ჯამი", "ზედნადები", "გეგმიური", "დაგროვება", "total", "overhead"];
  const isSummary = (row) => {
    const t = (row || []).slice(0, 13).map((c) => String(c || "").trim().toLowerCase()).join(" ");
    return summaryKeywords.some((k) => t.includes(k));
  };
  const isEmpty = (row) => (row || []).slice(1, 13).every((c) => String(c || "").trim() === "");

  let summaryStart = -1;
  for (let i = 8; i < used.values.length; i += 1) {
    if (isSummary(used.values[i])) { summaryStart = i; break; }
  }
  const limit = summaryStart === -1 ? used.values.length : summaryStart;

  let blockStart = -1, blockLen = 0;
  for (let i = 8; i < limit; i += 1) {
    if (isEmpty(used.values[i])) {
      if (blockStart === -1) blockStart = i;
      blockLen += 1;
      if (blockLen >= rowsToInsert) return blockStart + 1;
    } else if (blockStart !== -1) break;
  }
  if (summaryStart !== -1) {
    const summaryRow = summaryStart + 1;
    const missing = blockStart !== -1 ? rowsToInsert - blockLen : rowsToInsert;
    if (missing > 0) {
      sheet.getRange(`${summaryRow}:${summaryRow + missing - 1}`).insert(Excel.InsertShiftDirection.down);
      await context.sync();
    }
    return blockStart !== -1 ? blockStart + 1 : summaryRow;
  }
  return used.rowCount + 1;
}

export default function Tier1ResourceLookup({
  token, projectArea = 0, lang: initialLang = "geo", tier = 1,
}) {
  const [catalogs, setCatalogs]       = useState([]);
  const [catalogId, setCatalogId]     = useState("");
  const [workGroups, setWorkGroups]   = useState([]);
  const [search, setSearch]           = useState("");
  const [baseWorks, setBaseWorks]     = useState([]);
  const [lang, setLang]               = useState(initialLang);

  const [activeGroupId, setActiveGroupId] = useState(null);
  const [groupWorks, setGroupWorks]       = useState([]);
  const [choices, setChoices]             = useState({});

  const [bwDetails, setBwDetails]         = useState(null);
  const [excluded, setExcluded]           = useState(new Set());
  const [showResources, setShowResources] = useState(false);

  const [productOverrides, setProductOverrides] = useState(new Map());
  const [editingMaterial, setEditingMaterial]   = useState(null);
  const [productSearch, setProductSearch]       = useState("");
  const [productResults, setProductResults]     = useState([]);
  const [loadingProducts, setLoadingProducts]   = useState(false);

  const [offerOverrides, setOfferOverrides] = useState(new Map());
  const [offerPickerFor, setOfferPickerFor] = useState(null);
  const [productOffers, setProductOffers]   = useState(new Map());
  const [loadingOffers, setLoadingOffers]   = useState(false);

  const [quantity, setQuantity] = useState("");
  // unit → last quantity typed, so m² and m³ works don't share one value
  const [lastQuantityByUnit, setLastQuantityByUnit] = useState(new Map());
  const [activeSheetName, setActiveSheetName] = useState("");

  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const resetWork = useCallback(() => {
    setBwDetails(null);
    setExcluded(new Set());
    setProductOverrides(new Map());
    setOfferOverrides(new Map());
    setEditingMaterial(null);
    setOfferPickerFor(null);
    setShowResources(false);
  }, []);

  const closeGroup = useCallback(() => {
    setActiveGroupId(null);
    setGroupWorks([]);
    setChoices({});
    resetWork();
    setError("");
  }, [resetWork]);

  useEffect(() => {
    setCatalogId("");
    closeGroup();
  }, [tier, closeGroup]);

  useEffect(() => {
    if (!token) return;
    fetchTier1Catalogs(token, tier)
      .then((list) => setCatalogs(list || []))
      .catch((e) => setError(e?.message || "კატალოგები ვერ ჩაიტვირთა"));
  }, [token, tier]);

  useEffect(() => {
    if (!catalogId || !token) { setWorkGroups([]); return; }
    fetchTier1WorkGroups(token, catalogId, tier)
      .then((list) => setWorkGroups(list || []))
      .catch(() => setWorkGroups([]));
  }, [catalogId, token, tier]);

  useEffect(() => {
    if (!catalogId || !token) { setBaseWorks([]); return; }
    setBusy(true);
    const t = setTimeout(() => {
      fetchTier1BaseWorks(token, catalogId, { q: search, limit: 300, tier })
        .then((list) => setBaseWorks(list || []))
        .catch((e) => setError(e?.message || "ძებნა ვერ შესრულდა"))
        .finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [catalogId, token, search, tier]);

  const refreshActiveSheet = useCallback(async () => {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load("name");
        await context.sync();
        setActiveSheetName(sheet.name);
      });
    } catch { /* outside Excel the name is only informational */ }
  }, []);

  useEffect(() => {
    refreshActiveSheet();
    window.addEventListener("focus", refreshActiveSheet);
    return () => window.removeEventListener("focus", refreshActiveSheet);
  }, [refreshActiveSheet]);

  const searchGroups = useMemo(() => groupSearchResults(baseWorks, lang), [baseWorks, lang]);

  const activeGroup = useMemo(
    () => workGroups.find((g) => g.id === activeGroupId) || null,
    [workGroups, activeGroupId],
  );
  const activeGroupCard = useMemo(
    () => searchGroups.find((g) => g.id === activeGroupId) || null,
    [searchGroups, activeGroupId],
  );

  const openGroup = useCallback(async (group) => {
    setActiveGroupId(group.id);
    setChoices({});
    resetWork();
    setError("");
    setBusy(true);
    try {
      const list = await fetchTier1BaseWorks(token, catalogId, { groupCode: group.code, limit: 500, tier });
      setGroupWorks(list || []);
    } catch (e) {
      setError(e?.message || "სამუშაოები ვერ ჩაიტვირთა");
    } finally {
      setBusy(false);
    }
  }, [token, catalogId, tier, resetWork]);

  const { steps: levelSteps, resolved } = useMemo(
    () => deriveSteps(groupWorks, activeGroup, choices, lang),
    [groupWorks, activeGroup, choices, lang],
  );

  const chooseOption = (stepIndex, value) => {
    const kept = {};
    levelSteps.slice(0, stepIndex).forEach((s) => { if (!s.auto) kept[s.key] = s.chosen; });
    kept[levelSteps[stepIndex].key] = value;
    setChoices(kept);
  };

  const resolvedId = resolved?.id;
  useEffect(() => {
    resetWork();
    if (!resolvedId) return;
    let cancelled = false;
    setBusy(true);
    resolveBaseWorkRequirements(token, resolvedId, tier)
      .then((data) => {
        if (cancelled) return;
        const resources = (data?.items || []).map(mapResolvedItem);
        setBwDetails({ base_work: data?.base_work || resolved, resources });
        const unit = (data?.base_work || resolved)?.base_unit || "";
        const remembered = lastQuantityByUnit.get(unit);
        setQuantity(remembered ?? (isAreaUnit(unit) && projectArea ? String(projectArea) : ""));
      })
      .catch((e) => !cancelled && setError(e?.message || "რესურსები ვერ ჩაიტვირთა"))
      .finally(() => !cancelled && setBusy(false));
    return () => { cancelled = true; };
    // resolved/lastQuantityByUnit are read once per newly resolved work
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedId, token, tier, resetWork, projectArea]);

  const resources = bwDetails?.resources || [];
  const isMaterialDefined = (r) => productOverrides.has(r.id) || !r.missing_product;
  const genericResources = resources.filter((r) => (r.is_generic || r.missing_product) && !excluded.has(r.id));
  const definedGenericCount = genericResources.filter(isMaterialDefined).length;
  const includedResources = resources.filter((r) => !excluded.has(r.id) && isMaterialDefined(r));

  const baseUnitFactor = useMemo(() => {
    const v = Number(bwDetails?.base_work?.base_unit_value);
    return Number.isFinite(v) && v > 0 ? v : 1;
  }, [bwDetails]);

  const effectivePrice = (r) => {
    const price = offerOverrides.get(r.id)?.unit_rate ?? r.unit_rate;
    return price == null ? null : Number(price);
  };

  const costPerNorm = includedResources.reduce((acc, r) => {
    const price = effectivePrice(r);
    if (price == null) return acc;
    const amount = (Number(r.quantity_per_unit) || 0) * price;
    acc.total += amount;
    acc[r.resource_type] = (acc[r.resource_type] || 0) + amount;
    return acc;
  }, { total: 0 });
  const unpricedCount = includedResources.filter((r) => effectivePrice(r) == null).length;

  const quantityValue = Number(quantity);
  const quantityValid = Number.isFinite(quantityValue) && quantityValue > 0;
  const lineTotal = quantityValid ? (costPerNorm.total * quantityValue) / baseUnitFactor : 0;

  const pendingLevel = levelSteps.find((s) => !s.chosen);
  const pendingAction = (() => {
    if (!activeGroupId) return null;
    if (pendingLevel) return `აირჩიეთ: ${pendingLevel.label}`;
    if (!bwDetails) return "იტვირთება…";
    if (definedGenericCount < genericResources.length) return "განსაზღვრეთ მასალები";
    if (includedResources.length === 0) return "მონიშნეთ რესურსები";
    if (!quantityValid) return "მიუთითეთ რაოდენობა";
    return null;
  })();

  const loadProducts = useCallback(async (resource, q = "") => {
    setLoadingProducts(true);
    try {
      const list = await fetchProducts(token, {
        kind: resource.resource_type || null,
        classificationCode: resource.classification_code || null,
        isGeneric: false,
        q,
        limit: 100,
      });
      setProductResults(list || []);
    } catch {
      setProductResults([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [token]);

  const startEditingMaterial = (resource) => {
    setEditingMaterial(resource.id);
    setProductSearch("");
    setProductResults([]);
    loadProducts(resource);
  };

  const pickProduct = useCallback(async (resource, product) => {
    setProductOverrides((prev) => new Map(prev).set(resource.id, {
      product_id: product.id,
      display_id: product.display_id,
      name_geo: product.name_geo,
      name_eng: product.name_eng,
      name_rus: product.name_rus,
      base_unit: product.base_unit,
    }));
    setEditingMaterial(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const offers = await fetchOffers(token, { productId: product.id, activeOn: today });
      setProductOffers((prev) => new Map(prev).set(product.id, offers || []));
      setOfferOverrides((prev) => {
        const next = new Map(prev);
        if (offers?.length) {
          const best = offers.find((o) => o.is_preferred) || offers.reduce((a, b) => (a.unit_rate <= b.unit_rate ? a : b));
          next.set(resource.id, {
            offer_id: best.id, unit_rate: best.unit_rate, currency: best.currency,
            supplier_name: best.supplier_name, is_preferred: best.is_preferred,
          });
        } else {
          next.delete(resource.id);
        }
        return next;
      });
    } catch { /* the product stays chosen; it is shown as unpriced */ }
  }, [token]);

  const toggleOfferPicker = useCallback(async (resource) => {
    const pid = productOverrides.get(resource.id)?.product_id ?? resource.product_id;
    if (!pid) return;
    if (offerPickerFor === resource.id) { setOfferPickerFor(null); return; }
    setOfferPickerFor(resource.id);
    if (productOffers.has(pid)) return;
    setLoadingOffers(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const list = await fetchOffers(token, { productId: pid, activeOn: today });
      setProductOffers((prev) => new Map(prev).set(pid, list || []));
    } catch {
      setProductOffers((prev) => new Map(prev).set(pid, []));
    } finally {
      setLoadingOffers(false);
    }
  }, [token, offerPickerFor, productOffers, productOverrides]);

  const pickOffer = (resource, offer) => {
    setOfferOverrides((prev) => new Map(prev).set(resource.id, {
      offer_id: offer.id, unit_rate: offer.unit_rate, currency: offer.currency,
      supplier_name: offer.supplier_name, is_preferred: offer.is_preferred,
    }));
    setOfferPickerFor(null);
  };

  const toggleExcluded = (id) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const insertIntoSheet = useCallback(async () => {
    if (pendingAction || !bwDetails) return;
    const bw = bwDetails.base_work;
    const userVolume = quantityValue;
    setBusy(true);
    setError("");
    try {
      const inserted = await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load("name");
        const used = sheet.getUsedRangeOrNullObject();
        used.load(["values", "rowCount"]);
        await context.sync();

        let maxStdNo = 0;
        const existingPairs = new Set();
        let currentBwCode = "";
        if (!used.isNullObject) {
          for (let i = 8; i < used.values.length; i += 1) {
            const codeCell = String(used.values[i][12] || "").trim();
            const nVal = String(used.values[i][0] || "").trim();
            const headerMatch = nVal.match(/^(\d+)(?:\.0)?$/);
            if (headerMatch) {
              currentBwCode = codeCell;
              const v = parseInt(headerMatch[1], 10);
              if (Number.isFinite(v) && v > maxStdNo) maxStdNo = v;
            } else if (codeCell && currentBwCode) {
              existingPairs.add(`${currentBwCode}::${codeCell}`);
            }
          }
        }

        const codeOf = (r) => productOverrides.get(r.id)?.display_id || r.code || "";
        const newResources = includedResources.filter((r) => !existingPairs.has(`${bw.code}::${codeOf(r)}`));
        if (newResources.length === 0) {
          throw new Error(`"${bw.code}"-ის ყველა რესურსი უკვე არის ამ ფურცელზე.`);
        }

        const normUnitDisplay = formatBaseUnitDisplay(bw);
        const headerRow = [
          String(maxStdNo + 1),
          pickName(bw, lang) || bw.code,
          bw.base_unit || normUnitDisplay,
          "",
          userVolume,
          "", "", "", "", "", "", "",
          bw.code || "",
        ];

        const resourceRows = newResources.map((r) => {
          const prodOvr = productOverrides.get(r.id);
          const price = effectivePrice(r) ?? "";
          const name = prodOvr
            ? (prodOvr[`name_${lang}`] || prodOvr.name_geo || prodOvr.name_eng || prodOvr.display_id || r.code)
            : (pickName(r, lang) || r.code);
          return [
            "",
            name,
            prodOvr?.base_unit || r.base_unit || "",
            normUnitDisplay,
            "",
            r.resource_type === "MATERIAL" ? price : "",
            "",
            r.resource_type === "LABOUR" ? price : "",
            "",
            r.resource_type === "EQUIPMENT" ? price : "",
            "",
            "",
            codeOf(r),
          ];
        });
        const trailerRow = Array(13).fill("");
        const safeCell = (v) => {
          if (v === null || v === undefined) return "";
          if (typeof v === "number" && !Number.isFinite(v)) return "";
          return v;
        };
        const rows = [headerRow, ...resourceRows, trailerRow].map((row) => row.map(safeCell));

        const insertRow = await findInsertRow(sheet, context, rows.length);
        const range = sheet.getRangeByIndexes(insertRow - 1, 0, rows.length, 13);
        sheet.getRangeByIndexes(insertRow - 1, 12, rows.length, 1).numberFormat = rows.map(() => ["@"]);
        range.values = rows;
        range.format.wrapText = true;
        range.format.verticalAlignment = "Center";

        const headerRange = sheet.getRangeByIndexes(insertRow - 1, 0, 1, 13);
        headerRange.format.font.bold = true;
        headerRange.format.fill.color = "#0F766E";
        headerRange.format.font.color = "#FFFFFF";

        const volumeCell = sheet.getRangeByIndexes(insertRow - 1, 4, 1, 1);
        volumeCell.numberFormat = [["#,##0.###"]];
        volumeCell.format.fill.color = "#FEF3C7";
        volumeCell.format.font.color = "#0F172A";

        newResources.forEach((r, idx) => {
          const rowNum = insertRow + idx + 1;
          const normQty = Number(r.quantity_per_unit) || 0;
          sheet.getRangeByIndexes(rowNum - 1, 4, 1, 1).formulas =
            [[`=${normQty}*E$${insertRow}/${baseUnitFactor}`]];
          const totalCol = { MATERIAL: ["G", "F", 6], LABOUR: ["I", "H", 8], EQUIPMENT: ["K", "J", 10] }[r.resource_type];
          if (totalCol) {
            const [, priceCol, colIdx] = totalCol;
            sheet.getRangeByIndexes(rowNum - 1, colIdx, 1, 1).formulas =
              [[`=IF(OR(${priceCol}${rowNum}="",E${rowNum}=""),0,${priceCol}${rowNum}*E${rowNum})`]];
          }
          sheet.getRangeByIndexes(rowNum - 1, 11, 1, 1).formulas = [[`=SUM(G${rowNum},I${rowNum},K${rowNum})`]];
        });
        sheet.getRangeByIndexes(insertRow, 4, newResources.length, 8).numberFormat =
          newResources.map(() => Array(8).fill("#,##0.000"));

        range.format.borders.getItem("EdgeBottom").style = "Continuous";
        range.format.borders.getItem("EdgeBottom").color = "#cccccc";
        range.format.autofitRows();
        await context.sync();

        return {
          sheetName: sheet.name,
          insertRow,
          rowCount: rows.length,
          added: newResources.length,
          skipped: includedResources.length - newResources.length,
        };
      });

      setLastQuantityByUnit((prev) => new Map(prev).set(bw.base_unit || "", String(userVolume)));
      const skippedNote = inserted.skipped ? ` · ${inserted.skipped} უკვე იყო ფურცელზე` : "";
      setToast({
        message: `ჩაემატა ${bw.code}: ${inserted.added} რესურსი, სტრიქონი ${inserted.insertRow}${skippedNote}`,
        undo: inserted,
      });
    } catch (e) {
      setError(e?.message || "ფურცელზე ჩამატება ვერ მოხერხდა");
    } finally {
      setBusy(false);
    }
    // effectivePrice reads offerOverrides, which is listed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAction, bwDetails, quantityValue, includedResources, productOverrides, offerOverrides, lang, baseUnitFactor]);

  const undoInsert = async () => {
    const info = toast?.undo;
    setToast(null);
    if (!info) return;
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(info.sheetName);
        sheet.getRangeByIndexes(info.insertRow - 1, 0, info.rowCount, 13).clear();
        await context.sync();
      });
    } catch (e) {
      setError(e?.message || "გაუქმება ვერ მოხერხდა");
    }
  };

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(t);
  }, [toast]);

  const workCard = activeGroupCard || (resolved && {
    code: resolved.work_group_code,
    unit: formatBaseUnitDisplay(resolved),
    name: pickGroupName(resolved, lang),
  });

  let stepNo = 0;
  const nextStepNo = () => { stepNo += 1; return stepNo; };

  return (
    <div className="sheet-lookup-panel aw-panel" data-no-translate="true">
      {!activeGroupId && (
        <>
          <div className="aw-field">
            <label className="aw-field-label">ნორმატიული ბაზა</label>
            <select
              value={catalogId}
              onChange={(e) => { setCatalogId(e.target.value); setError(""); }}
              className="sheet-lookup-select"
            >
              <option value="">აირჩიეთ კატალოგი…</option>
              {catalogs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.base_work_count} სამუშაო
                </option>
              ))}
            </select>
          </div>

          {catalogId && (
            <>
              <div className="aw-search-row">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="ძიება: კოდი ან დასახელება"
                  className="sheet-lookup-search"
                />
                <div className="aw-lang">
                  {LANGS.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => setLang(l.id)}
                      className={`sheet-lookup-chip sheet-lookup-chip--small ${lang === l.id ? "active" : ""}`}
                    >{l.label}</button>
                  ))}
                </div>
              </div>

              <div className="aw-results">
                {busy && <div className="aw-muted">იტვირთება…</div>}
                {!busy && searchGroups.length === 0 && (
                  <div className="aw-muted">სამუშაო ვერ მოიძებნა.</div>
                )}
                {searchGroups.map((g) => (
                  <button key={g.id} className="aw-work-card aw-work-card--clickable" onClick={() => openGroup(g)}>
                    <div className="aw-work-card-meta">
                      <span className="aw-code">{g.code}</span>
                      {g.unit && <span className="aw-unit">{g.unit}</span>}
                      {g.count > 1 && <span className="aw-muted">{g.count} ვარიანტი</span>}
                    </div>
                    <div className="aw-work-card-name">{g.name}</div>
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {activeGroupId && (
        <>
          <button className="aw-back" onClick={closeGroup}>‹ ძიების შედეგები</button>

          {workCard && (
            <div className="aw-work-card">
              <div className="aw-work-card-meta">
                <span className="aw-code">{workCard.code}</span>
                {workCard.unit && <span className="aw-unit">{workCard.unit}</span>}
              </div>
              <div className="aw-work-card-name">{workCard.name}</div>
            </div>
          )}

          {busy && groupWorks.length === 0 && <div className="aw-muted">იტვირთება…</div>}

          {levelSteps.map((step, idx) => (
            <Step
              key={step.key}
              no={nextStepNo()}
              title={step.label}
              tag={step.auto ? "ერთადერთი მნიშვნელობა" : step.tag}
              done={Boolean(step.chosen)}
            >
              <div className="aw-chips">
                {step.options.map((opt) => (
                  <button
                    key={opt.value}
                    disabled={step.auto}
                    className={`aw-chip ${step.chosen === opt.value ? "active" : ""} ${step.auto ? "auto" : ""}`}
                    onClick={() => chooseOption(idx, opt.value)}
                  >{opt.label}</button>
                ))}
              </div>
            </Step>
          ))}

          {resolved && bwDetails && genericResources.length > 0 && (
            <Step
              no={nextStepNo()}
              title="მასალების განსაზღვრა"
              tag={`${definedGenericCount} / ${genericResources.length}`}
              done={definedGenericCount === genericResources.length}
            >
              {genericResources.map((r) => {
                const prodOvr = productOverrides.get(r.id);
                const defined = isMaterialDefined(r);
                const editing = editingMaterial === r.id || !defined;
                const price = effectivePrice(r);
                return (
                  <div key={r.id} className={`aw-material ${defined && !editing ? "folded" : ""}`}>
                    <div className="aw-material-head">
                      <span className="aw-material-name">{pickName(r, lang) || r.code}</span>
                      <span className="aw-tag">ზოგადი მასალა</span>
                    </div>

                    {defined && !editing && (
                      <div className="aw-material-line">
                        <span className="aw-material-product">
                          {prodOvr
                            ? (prodOvr[`name_${lang}`] || prodOvr.name_geo || prodOvr.display_id)
                            : (pickName(r, lang) || r.code)}
                        </span>
                        <span className={price == null ? "aw-unpriced" : "aw-price"}>
                          {price == null ? "ფასი არ არის" : `${price} ₾/${prodOvr?.base_unit || r.base_unit}`}
                        </span>
                        <button className="aw-link" onClick={() => startEditingMaterial(r)}>შეცვლა</button>
                      </div>
                    )}

                    {editing && (
                      <div className="aw-picker">
                        <input
                          placeholder="ძიება: დასახელება ან კოდი"
                          value={productSearch}
                          onChange={(e) => { setProductSearch(e.target.value); loadProducts(r, e.target.value); }}
                          onFocus={() => editingMaterial !== r.id && startEditingMaterial(r)}
                          className="sheet-lookup-search"
                        />
                        {editingMaterial === r.id && (
                          <div className="aw-picker-list">
                            {loadingProducts && <div className="aw-muted">იტვირთება…</div>}
                            {!loadingProducts && productResults.length === 0 && (
                              <div className="aw-unpriced">
                                კონკრეტული პროდუქტი ვერ მოიძებნა. დაამატეთ რესურსების კატალოგში.
                              </div>
                            )}
                            {productResults.map((p) => (
                              <button key={p.id} className="aw-picker-item" onClick={() => pickProduct(r, p)}>
                                <span className="aw-code">{p.display_id || `#${p.id}`}</span>
                                <span className="aw-picker-name">{p[`name_${lang}`] || p.name_geo || p.name_eng || "—"}</span>
                                <span className="aw-muted">{p.base_unit}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </Step>
          )}

          {resolved && bwDetails && (
            <Step
              no={nextStepNo()}
              title="რესურსები"
              tag={`${includedResources.length} / ${resources.length}`}
              done={includedResources.length > 0}
              collapsed={!showResources}
              onToggle={() => setShowResources((v) => !v)}
            >
              {resources.length === 0 && <div className="aw-muted">ამ სამუშაოს რესურსები ჯერ არ აქვს.</div>}
              {TYPES.map((t) => {
                const rows = resources.filter((r) => r.resource_type === t.id);
                if (rows.length === 0) return null;
                return (
                  <div key={t.id} className="aw-res-group">
                    <div className="aw-res-group-title" style={{ color: t.color }}>{t.label}</div>
                    {rows.map((r) => {
                      const defined = isMaterialDefined(r);
                      const prodOvr = productOverrides.get(r.id);
                      const offerOvr = offerOverrides.get(r.id);
                      const price = effectivePrice(r);
                      const supplier = offerOvr?.supplier_name ?? r.supplier_name;
                      const preferred = offerOvr?.is_preferred ?? r.is_preferred;
                      const productId = prodOvr?.product_id ?? r.product_id;
                      const offers = productId ? (productOffers.get(productId) || []) : [];
                      return (
                        <div key={r.id} className="aw-res-row">
                          <label className={`aw-res-line ${excluded.has(r.id) ? "disabled" : ""}`}>
                            <input
                              type="checkbox"
                              checked={!excluded.has(r.id)}
                              onChange={() => toggleExcluded(r.id)}
                            />
                            <span className="aw-res-name">
                              {prodOvr ? (prodOvr[`name_${lang}`] || prodOvr.name_geo) : (pickName(r, lang) || r.code)}
                            </span>
                            <span className="aw-muted">{r.quantity_per_unit} {prodOvr?.base_unit || r.base_unit}</span>
                            {r.unit_converted && !prodOvr && (
                              <span className="aw-muted" title={`გადათვლილია ${r.requirement_unit} → ${r.base_unit}`}>⇄ {r.requirement_unit}→{r.base_unit}</span>
                            )}
                            {r.unit_mismatch && !prodOvr && (
                              <span className="aw-unpriced" title="მოთხოვნის და პროდუქტის ერთეულები არ ემთხვევა და ვერ გადაითვალა — შეამოწმე ფასი">⚠ ერთეული</span>
                            )}
                            <span className={price == null ? "aw-unpriced" : "aw-price"}>
                              {price == null ? "ფასი არ არის" : `${price} ₾`}
                            </span>
                          </label>
                          {productId && defined && (
                            <div className="aw-res-supplier">
                              <button className="aw-link" onClick={() => toggleOfferPicker(r)}>
                                {supplier ? `${preferred ? "★ " : ""}${supplier}` : "მომწოდებელი"} ▾
                              </button>
                            </div>
                          )}
                          {offerPickerFor === r.id && (
                            <div className="aw-picker-list">
                              {loadingOffers && <div className="aw-muted">იტვირთება…</div>}
                              {!loadingOffers && offers.length === 0 && (
                                <div className="aw-unpriced">აქტიური შეთავაზება არ არის.</div>
                              )}
                              {offers.map((o) => (
                                <button key={o.id} className="aw-picker-item" onClick={() => pickOffer(r, o)}>
                                  <span>{o.is_preferred ? "★" : "·"}</span>
                                  <span className="aw-picker-name">{o.supplier_name}</span>
                                  <span className="aw-price">{o.unit_rate} {o.currency}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </Step>
          )}

          {resolved && bwDetails && (
            <Step no={nextStepNo()} title="რაოდენობა" tag={bwDetails.base_work?.base_unit} done={quantityValid}>
              <div className="aw-qty">
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && insertIntoSheet()}
                  placeholder="მაგ. 250"
                  className="sheet-lookup-search"
                />
                <span className="aw-unit">{bwDetails.base_work?.base_unit}</span>
              </div>
              {quantity !== "" && !quantityValid && (
                <div className="aw-unpriced">შეიყვანეთ რიცხვი 0-ზე მეტი, მაგ. 250</div>
              )}
              {quantityValid && baseUnitFactor !== 1 && (
                <div className="aw-muted">
                  {quantityValue} {bwDetails.base_work?.base_unit} = {(quantityValue / baseUnitFactor).toLocaleString("en-US", { maximumFractionDigits: 4 })} × {formatBaseUnitDisplay(bwDetails.base_work)}
                </div>
              )}

              <div className="aw-summary">
                <div className="aw-summary-row">
                  <span>{formatBaseUnitDisplay(bwDetails.base_work)}-ზე</span>
                  <b>{formatMoney(costPerNorm.total)}</b>
                </div>
                {TYPES.map((t) => costPerNorm[t.id] ? (
                  <div key={t.id} className="aw-summary-row aw-muted">
                    <span>{t.label}</span><span>{formatMoney(costPerNorm[t.id])}</span>
                  </div>
                ) : null)}
                {quantityValid && (
                  <div className="aw-summary-row aw-summary-total">
                    <span>ჯამი, დანარიცხების გარეშე</span>
                    <b>{formatMoney(lineTotal)}</b>
                  </div>
                )}
                {unpricedCount > 0 && (
                  <div className="aw-unpriced">{unpricedCount} რესურსს ფასი არ აქვს და ჯამში არ ითვლება.</div>
                )}
              </div>
            </Step>
          )}

          {error && <div className="sheet-lookup-error">{error}</div>}

          <div className="aw-footer">
            <div className="aw-footer-target">
              ჩაემატება: <b>{activeSheetName || "აქტიური ფურცელი"}</b>
            </div>
            <button
              className="aw-insert"
              disabled={Boolean(pendingAction) || busy}
              onClick={insertIntoSheet}
            >
              {busy ? "…" : (pendingAction || "ჩამატება ცხრილში")}
            </button>
          </div>
        </>
      )}

      {!activeGroupId && error && <div className="sheet-lookup-error">{error}</div>}

      {toast && (
        <div className="aw-toast">
          <span>{toast.message}</span>
          <button className="aw-link" onClick={undoInsert}>გაუქმება</button>
        </div>
      )}
    </div>
  );
}

function Step({ no, title, tag, done, collapsed = false, onToggle, children }) {
  return (
    <div className={`aw-step ${done ? "done" : ""}`}>
      <div className={`aw-step-head ${onToggle ? "clickable" : ""}`} onClick={onToggle}>
        <span className="aw-step-marker">{done ? "✓" : no}</span>
        <span className="aw-step-title">{title}</span>
        {tag && <span className="aw-tag">{tag}</span>}
        {onToggle && <span className="aw-muted">{collapsed ? "▸" : "▾"}</span>}
      </div>
      {!collapsed && <div className="aw-step-body">{children}</div>}
    </div>
  );
}
